/**
 * Event API service.
 * Provides category and customer group data from the backend.
 */
import { CUSTOMER_GROUPS, CATEGORIES, CATEGORIES_BY_ID, CATEGORIES_BY_KEY } from '../../../shared/constants/api';
import { CATEGORY_SLUG_LOCALES } from '../../../shared/constants/event.constants';
import * as api from '../../../shared/services/apiClient';
import type { Category } from '../../../shared/types/models';
import type { CustomerGroup } from '../types/home.types';

/**
 * Fetches all customer groups from Commercetools.
 * Returns null on failure (caller decides how to handle).
 */
export const fetchCustomerGroups = async (): Promise<{ results: CustomerGroup[] } | null> => {
    try {
        const res = await api.get(CUSTOMER_GROUPS);
        if (!res.ok) throw new Error(`Failed to fetch customer groups: ${res.status}`);
        return await res.json();
    } catch (err) {
        console.error('[eventApi] fetchCustomerGroups failed:', err);
        return null;
    }
};

/**
 * Fetches a single category by ID.
 */
export const getCategoryById = async (categoryId: string): Promise<Category | null> => {
    try {
        const res = await api.get(CATEGORIES_BY_ID(categoryId));
        if (!res.ok) return null;
        return await res.json();
    } catch (err) {
        console.error(`[eventApi] getCategoryById failed for ID "${categoryId}":`, err);
        return null;
    }
};

/**
 * Fetches multiple categories by their IDs using a batch query.
 */
export const getCategoriesByIds = async (categoryIds: string[]): Promise<Category[]> => {
    if (!categoryIds || categoryIds.length === 0) return [];

    const uniqueIds = [...new Set(categoryIds)];
    const whereClause = `id in ("${uniqueIds.join('", "')}")`;

    try {
        const res = await api.get(CATEGORIES, {
            where: whereClause,
            limit: String(uniqueIds.length),
        });
        if (!res.ok) return [];
        const data = await res.json();
        return data.results || [];
    } catch (err) {
        console.error('[eventApi] getCategoriesByIds failed:', err);
        return [];
    }
};

/**
 * Resolves an event key to a Commercetools Category.
 *
 * Strategy:
 *  1. Try a direct key lookup via `/api/categories/key/:key` (O(1), preferred).
 *  2. On failure, fall back to a slug search across all supported locales.
 *
 * Returns null if the category cannot be resolved by either strategy.
 */
export const getCategoryByKey = async (key: string): Promise<Category | null> => {
    try {
        const res = await api.get(CATEGORIES_BY_KEY(key));
        if (res.ok) return await res.json();

        // Fallback: search by slug across all supported locales
        const slugFilter = CATEGORY_SLUG_LOCALES
            .map((locale) => `slug(${locale}="${key}")`)
            .join(' or ');

        const searchRes = await api.get(CATEGORIES, { where: slugFilter });
        if (!searchRes.ok) {
            console.error(`[eventApi] getCategoryByKey: could not resolve key or slug for "${key}"`);
            return null;
        }

        const data = await searchRes.json();
        return data.results?.[0] ?? null;
    } catch (err) {
        console.error(`[eventApi] getCategoryByKey failed for key "${key}":`, err);
        return null;
    }
};
